import { Link } from 'react-router-dom';
import { useEffect, useRef, useState, type RefObject } from 'react';
import type React from 'react';
import {
  ArrowRight,
  FileText,
  MessageSquare,
  ShieldCheck,
  Quote as QuoteIcon,
  Sparkles,
  Eye,
  Users,
} from 'lucide-react';
import { DiscloserMark } from '@/components/onboarding-v2/OnboardingFlowV2';
import type { VariantKey } from './content';
import HeroProductPreview from './HeroProductPreview';
import HeroHomeScene from './HeroHomeScene';

// ----------------------------------------------------------------------------
// Discloser landing page — deal-engine staircase, two variants.
//
// Same brand claim, two audience-specific flows:
//   /, /buyer → AGENTS variant  — both listing AND buyer's agents (the
//                                  insight: both sides create friction for
//                                  each other; the deal stalls in the gap)
//   /broker   → BROKERS variant — brokerage owner's POV
//
// Both variants render through one component reading CONTENT[variant].
// The skeleton is identical (5 sections — Hero, Problem, Solution, Proof,
// FinalCTA). Pricing lives on /upgrade. Copy, centerpiece, and final CTA
// differ per audience.
// ----------------------------------------------------------------------------

// ─── Per-variant accent (visual differentiation) ─────────────────────────────

type VariantAccent = {
  iconText: string;
  ctaFill: string;
  ctaHover: string;
  heroFrom: string;
  heroVia: string;
  heroTo: string;
  finalFrom: string;
  finalVia: string;
  finalTo: string;
};

const ACCENTS: Record<VariantKey, VariantAccent> = {
  seller: {
    iconText: 'text-brand-sky',
    ctaFill: 'bg-brand-ink',
    ctaHover: 'hover:bg-brand-ink-soft',
    heroFrom: 'from-brand-sky/35',
    heroVia: 'via-brand-cream/20',
    heroTo: 'to-transparent',
    finalFrom: 'from-brand-sky/55',
    finalVia: 'via-brand-sky-soft/45',
    finalTo: 'to-brand-cream/35',
  },
  buyer: {
    iconText: 'text-brand-accent',
    ctaFill: 'bg-brand-ink',
    ctaHover: 'hover:bg-brand-ink-soft',
    heroFrom: 'from-brand-accent/25',
    heroVia: 'via-brand-cream/30',
    heroTo: 'to-transparent',
    finalFrom: 'from-brand-accent/40',
    finalVia: 'via-brand-cream/40',
    finalTo: 'to-brand-paper-warm',
  },
  efficiency: {
    iconText: 'text-brand-info',
    ctaFill: 'bg-brand-ink',
    ctaHover: 'hover:bg-brand-ink-soft',
    heroFrom: 'from-brand-info-bg',
    heroVia: 'via-brand-cream/20',
    heroTo: 'to-transparent',
    finalFrom: 'from-brand-info/30',
    finalVia: 'via-brand-cream/30',
    finalTo: 'to-brand-paper-warm',
  },
};

// ─── Content schema ──────────────────────────────────────────────────────────

type StageIcon = 'doc' | 'msg' | 'shield' | 'eye' | 'users';

interface Stage {
  icon: StageIcon;
  title: string;
  body: React.ReactNode;
  // Optional pull-quote line used by the V6 anchor card (Agents Stage 02).
  // Renders in italic Playfair above the body.
  lede?: React.ReactNode;
}

interface VariantPageContent {
  hero: {
    eyebrow: string;
    h1Lead: string;
    h1Italic: string;
    subhead: React.ReactNode;
    ctaLabel: string;
    ctaHref: string;
    ctaSubtext: string;
  };
  problem: {
    eyebrow: string;
    title: React.ReactNode;
    body: React.ReactNode;
    handoff: string;
  };
  solution: {
    eyebrow: string;
    title: string;
    stages: Stage[];
    centerpiece: {
      label: string;
      setup: React.ReactNode;
      replyLabel: string;
      reply: React.ReactNode;
      footer: React.ReactNode;
    };
    handoff: string;
  };
  proof: {
    eyebrow: string;
    title: string;
    stats: { number: string; label: string; italic?: boolean }[];
    quote: string;
    // Optional substring of `quote` to wrap in the sky-highlight strong (V6 only).
    quoteEmphasis?: string;
    quoteAttribution: string;
    // Optional split attribution used by V6's mark+name+role layout. When set,
    // Proof renders these instead of `quoteAttribution`. Brokers omits these
    // and uses the legacy single-line attribution.
    quoteAuthorName?: string;
    quoteAuthorRole?: string;
    handoff: string;
  };
  finalCta: {
    title: React.ReactNode;
    subhead: string;
    ctaLabel: string;
    ctaHref: string;
    psLine: React.ReactNode;
  };
}

// ─── Content per variant ─────────────────────────────────────────────────────

// AGENTS — both listing AND buyer's agents share this flow. The insight:
// each side creates friction for the other (listing agent waiting on
// seller clarifications, buyer agent waiting on listing agent's reply).
// Discloser is the engine BETWEEN them — the same Closer servicing both
// sides in real time so neither side's hesitation kills the deal.
const AGENTS: VariantPageContent = {
  hero: {
    eyebrow: 'The deal engine for California real estate agents',
    h1Lead: 'Read Disclosures',
    h1Italic: '75% faster.',
    subhead: (
      <>
        Listing agent or buyer's agent — the deal stalls at the same place. Disclosure
        questions piling up between sides. Discloser reads the packet, answers the buyer's
        worry, drafts the email between you, and gets <em>both</em> agents to "ready" the
        same afternoon.
      </>
    ),
    ctaLabel: 'Try it on your next packet',
    ctaHref: '/onboarding',
    ctaSubtext: 'Free for buyer-side review. No credit card.',
  },
  problem: {
    eyebrow: 'Where the time goes',
    title: (
      <>
        Reading disclosures takes days.
        <br />
        <em className="font-display italic font-normal text-brand-ink-soft">Both sides lose momentum.</em>
      </>
    ),
    body: (
      <>
        <p className="font-brand text-base md:text-[16.5px] text-brand-text-2 leading-[1.65] max-w-[780px] mx-auto">
          Buyer's agents stall on 300-page packets. Listing agents stall on seller
          clarifications. Disclosures are the silent time-tax on every deal.
        </p>
        <p className="font-brand text-base md:text-[16.5px] text-brand-text-2 leading-[1.65] max-w-[780px] mx-auto mt-2.5">
          Discloser cuts it.{' '}
          <em className="font-display italic text-brand-ink">Buyers offer sooner. Sellers list sooner.</em>
        </p>
      </>
    ),
    handoff: 'Here\'s how Discloser cuts the time on both sides. ↓',
  },
  solution: {
    eyebrow: 'What Discloser does for both sides',
    title: 'Where Deals Progress',
    stages: [
      {
        icon: 'doc',
        title: 'Listing prep',
        body: (
          <>
            Drop in the seller's TDS + SPQ + AVID. Closer surfaces every Y-flag and
            contradiction, then drafts the clarification email to Sarah (your seller).
            Listing goes live tonight, not next Tuesday.
          </>
        ),
      },
      {
        icon: 'msg',
        title: 'The handoff',
        lede: 'Three days of email pong. One cited paragraph.',
        body: (
          <>
            Buyer's agent gets the packet. Buyer texts <em>"is Section II.A.6 a problem?"</em>{' '}
            Closer answers <strong>both</strong> at once: cited reply for the buyer, drafted
            email for the listing agent.
          </>
        ),
      },
      {
        icon: 'shield',
        title: 'Closing',
        body: (
          <>
            Every Q&amp;A timestamped. Every disclosure log saved. Every re-sign recorded.
            The audit trail your attorney would've asked for, already built.
          </>
        ),
      },
    ],
    centerpiece: {
      label: 'One question. Both sides handled.',
      setup: (
        <>
          Mark (buyer's agent) types into Closer:{' '}
          <em>"My buyer's worried about Section II.A.6 — can you check?"</em>
        </>
      ),
      replyLabel: 'Closer\'s response, working both sides at once:',
      reply: (
        <>
          Section II.A.6 is the standard California water-heater compliance language. Every
          CA listing has this. <strong>Not a flag.</strong> The real flag is Section
          II.A.10 — the AVID notes a missing seismic strap and Sarah (the seller) marked{' '}
          <em>"unknown."</em>
          <br />
          <br />
          I've answered Mark's buyer with citations. I've drafted Mark's email to John (the
          listing agent) about the strap. I've notified John with a pre-written reply ready
          to send. <strong>Both sides move now.</strong>
        </>
      ),
      footer: (
        <>
          Three days of email pong, compressed into 30 seconds. The deal moves before
          either agent finishes their next showing.{' '}
          <strong className="text-brand-ink">
            Both sides ready, same afternoon.
          </strong>
        </>
      ),
    },
    handoff: 'The same engine, both sides, all three stages. Here\'s what it\'s doing in real deals. ↓',
  },
  proof: {
    eyebrow: 'What it\'s doing for California agents',
    title: 'Deals close 5–8 days faster — for both sides of the table.',
    stats: [
      { number: '3,000', label: 'California packets processed across both sides since launch' },
      { number: '297', label: 'real flags surfaced that one side or both would have skimmed past' },
      { number: '97%', label: 'of agents put their next packet through too' },
      { number: '5–8d', label: 'faster close, both sides of the table', italic: true },
    ],
    quote: 'Discloser is a game changer. Finally AI for Real Estate Agents is here.',
    quoteEmphasis: 'game changer',
    quoteAttribution: '— Jake Serpa, Top 10% Bay Area listing agent',
    quoteAuthorName: 'Jake Serpa',
    quoteAuthorRole: 'Top 10% Bay Area listing agent, Coldwell Banker',
    handoff: 'Free for buyer-side review. Pro is $19/month. ↓',
  },
  finalCta: {
    title: (
      <>
        Your next deal closes <em className="font-display italic font-normal">faster.</em>
      </>
    ),
    subhead:
      'Whichever side of the table you\'re on, the disclosure layer is what stalls it. Compress the next packet into tonight.',
    ctaLabel: 'Start free',
    ctaHref: '/onboarding',
    psLine: (
      <>
        The disclosure phase is the longest stage of every deal where neither agent is
        getting paid. Discloser is the cheapest way to make it shorter — for both sides
        at once.
      </>
    ),
  },
};

const EFFICIENCY: VariantPageContent = {
  hero: {
    eyebrow: 'The deal engine for California brokerages',
    h1Lead: 'Disclosure is friction.',
    h1Italic: 'We are the engine.',
    subhead: (
      <>
        Every listing in your brokerage runs the same disclosure gauntlet. Discloser
        standardizes it: every agent, every packet, every audit trail. Compliance visible.
        Throughput up. E&O risk down.
      </>
    ),
    ctaLabel: 'Book a brokerage walkthrough',
    ctaHref: 'mailto:sales@discloser.co?subject=Brokerage walkthrough',
    ctaSubtext: '15-minute call. We show you the dashboard with sample data.',
  },
  problem: {
    eyebrow: 'Your hidden liability',
    title: (
      <>
        Your agents' disclosure work is invisible to you.
        <br />
        Until something breaks.
      </>
    ),
    body: (
      <>
        <p className="font-brand text-base md:text-lg text-brand-text-2 leading-relaxed mb-3 max-w-xl mx-auto">
          You're trusting 30 agents to do the same disclosure work, the same way, with the
          same care. Some are great. Some are skimming a 300-page packet at 11pm. You don't
          know which is which until a buyer's attorney files.
        </p>
        <p className="font-brand text-base md:text-lg text-brand-text-2 leading-relaxed max-w-xl mx-auto">
          When that happens, the agent's E&amp;O carrier walks. The lawsuit is on the
          brokerage. The reputation pays interest for years.
        </p>
      </>
    ),
    handoff: 'Here\'s what visibility across your brokerage looks like. ↓',
  },
  solution: {
    eyebrow: 'What Discloser does for brokerages',
    title: 'One disclosure standard. Every agent. Every listing.',
    stages: [
      {
        icon: 'doc',
        title: 'Per-agent disclosure quality',
        body: (
          <>
            Every agent in your brokerage runs their packets through the same Closer. Every
            Y-flag caught the same way. Every audit trail built the same way. The day-1
            hire produces the same disclosure quality as your top producer.
          </>
        ),
      },
      {
        icon: 'eye',
        title: 'Broker dashboard',
        body: (
          <>
            See every active listing's disclosure state across the brokerage. Which packets
            are clean. Which have unanswered flags. Which agents need a review. Compliance
            becomes visible without you reading 300 pages a week.
          </>
        ),
      },
      {
        icon: 'users',
        title: 'Audit trail at scale',
        body: (
          <>
            If a buyer's attorney comes calling, the timeline of every Q&amp;A, every
            signature, every re-sign is one query away. The brokerage's defense is built
            before you knew you needed it.
          </>
        ),
      },
    ],
    centerpiece: {
      label: 'Your brokerage dashboard, Monday morning',
      setup: <em>"Where do my listings stand on disclosure right now?"</em>,
      replyLabel: 'Closer\'s summary, refreshed live:',
      reply: (
        <>
          12 active listings across the brokerage. <strong>9 disclosure-clean.</strong>
          <br />
          <br />
          ⚠ <strong>142 Maple Ave (Sarah K.)</strong> — buyer-agent question stalled 2 days.
          I drafted the reply, awaiting Sarah's review.
          <br />
          ⚠ <strong>88 Elm Street (Marcus T.)</strong> — AVID contradicts seller's TDS.
          Flagged 3 days ago, seller hasn't been re-emailed. I've drafted the email; Marcus
          needs to send it.
          <br />
          ✓ Other 7 listings: disclosure clean, audit trail current.
        </>
      ),
      footer: (
        <>
          Your agents are still doing the work. You finally see the work happening.{' '}
          <strong className="text-brand-ink">
            One missed flag becomes a Slack notification, not a lawsuit.
          </strong>
        </>
      ),
    },
    handoff: 'What it\'s doing for the brokerages running it. ↓',
  },
  proof: {
    eyebrow: 'What it\'s doing for California brokerages',
    title: 'Your agents close faster. Your liability shrinks.',
    stats: [
      { number: '64%', label: 'reduction in disclosure-related E&O claims, year over year' },
      { number: '5', label: 'days faster average listing-to-live across active brokerages' },
    ],
    quote:
      'We onboarded four new agents last quarter and didn\'t worry about their first listing. That\'s never been true before.',
    quoteAttribution: '— Christine Park, Managing Broker, Bay Pacific Realty',
    handoff: 'Custom pricing for brokerages. Talk to us. ↓',
  },
  finalCta: {
    title: 'Your brokerage\'s compliance shouldn\'t be invisible to you.',
    subhead:
      'We onboard new brokerages with a 1-week side-by-side. Your top 3 listing agents run their next packet through Closer. You see the dashboard. You decide.',
    ctaLabel: 'Book the walkthrough',
    ctaHref: 'mailto:sales@discloser.co?subject=Brokerage walkthrough',
    psLine: (
      <>
        <strong className="text-brand-ink">PS</strong> — The brokerage that makes its
        agents' disclosures bulletproof outlives the one that hopes for the best. Pick on
        that math.
      </>
    ),
  },
};

// Both seller and buyer URLs render the unified AGENTS content.
// Brokerage URL renders BROKERS content. The deal-engine insight made
// the seller/buyer split obsolete: friction is mutual, and so is the
// fix.
const BROKERS = EFFICIENCY;

const CONTENT: Record<VariantKey, VariantPageContent> = {
  seller: AGENTS,
  buyer: AGENTS,
  efficiency: BROKERS,
};

// ─── Main component ──────────────────────────────────────────────────────────

interface Props {
  variant: VariantKey;
}

export default function LandingPage({ variant }: Props) {
  const accent = ACCENTS[variant];
  const content = CONTENT[variant];
  const isAgents = variant !== 'efficiency';

  return (
    <div className="min-h-screen flex flex-col bg-brand-paper-warm font-brand text-brand-ink">
      <MarketingHeader accent={accent} variant={variant} />
      {isAgents && <PromoStrip />}
      <main className="flex-grow">
        <Hero accent={accent} content={content.hero} />
        <TrustStrip />
        <Problem content={content.problem} variant={variant} />
        <Solution variant={variant} accent={accent} content={content.solution} />
        <section className="bg-brand-paper-warm">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6 pb-12 md:pb-20">
            <HeroProductPreview variant={variant === 'efficiency' ? 'brokers' : 'agents'} />
          </div>
        </section>
        <Proof content={content.proof} variant={variant} />
        <FinalCTA accent={accent} content={content.finalCta} variant={variant} />
      </main>
      <MarketingFooter />
    </div>
  );
}

// ─── Header ──────────────────────────────────────────────────────────────────

function MarketingHeader({
  accent,
  variant,
}: {
  accent: VariantAccent;
  variant: VariantKey;
}) {
  const isAgents = variant !== 'efficiency';
  return (
    <header className="sticky top-0 z-40 border-b border-black/5 bg-brand-paper-warm/85 backdrop-blur">
      <div className="max-w-[1280px] mx-auto px-4 md:px-6 h-14 flex items-center justify-between">
        <Link
          to="/"
          className="flex items-center gap-2 rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-ink/40"
          aria-label="Discloser — home"
        >
          <DiscloserMark size={39} />
          <span className="font-display text-[27px] font-semibold text-brand-ink tracking-tight">
            Discloser
          </span>
        </Link>
        <nav className="hidden md:flex items-center gap-1.5 font-meta text-[13px] text-brand-text-2">
          {/* Audience switcher — segmented-control between Agents and Brokerages */}
          <div className="inline-flex items-center rounded-pill bg-brand-ink/[0.05] p-1 gap-0.5">
            <Link
              to="/"
              className={`inline-flex items-center h-7 px-3 rounded-pill text-[12.5px] transition-colors ${
                isAgents
                  ? 'bg-white text-brand-ink font-semibold shadow-[0_1px_2px_rgba(26,22,21,0.06)]'
                  : 'text-brand-text-3 hover:text-brand-ink'
              }`}
            >
              For agents
            </Link>
            <Link
              to="/broker"
              className={`inline-flex items-center h-7 px-3 rounded-pill text-[12.5px] transition-colors ${
                !isAgents
                  ? 'bg-white text-brand-ink font-semibold shadow-[0_1px_2px_rgba(26,22,21,0.06)]'
                  : 'text-brand-text-3 hover:text-brand-ink'
              }`}
            >
              For brokerages
            </Link>
          </div>
          <Link to="/upgrade" className="inline-flex items-center h-10 px-3 rounded-pill hover:text-brand-ink hover:bg-brand-ink/5 transition-colors">
            Pricing
          </Link>
          <Link to="/walkthrough?tour=auto" className="inline-flex items-center h-10 px-3 rounded-pill hover:text-brand-ink hover:bg-brand-ink/5 transition-colors">
            Walkthrough
          </Link>
          <Link to="/blog" className="inline-flex items-center h-10 px-3 rounded-pill hover:text-brand-ink hover:bg-brand-ink/5 transition-colors">
            Blog
          </Link>
        </nav>
        <div className="flex items-center gap-2">
          <Link
            to="/auth"
            className="hidden sm:inline-flex font-meta text-[13px] text-brand-text-2 hover:text-brand-ink h-9 px-3 items-center"
          >
            Sign in
          </Link>
          <Link
            to="/onboarding"
            className={`inline-flex items-center gap-1.5 rounded-pill text-white font-brand text-sm font-semibold h-9 px-4 transition-colors active:scale-[0.97] ${accent.ctaFill} ${accent.ctaHover}`}
          >
            Try free <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>
      <nav className="md:hidden border-t border-black/5 px-4 py-2 flex items-center gap-1 overflow-x-auto font-meta text-[12px] text-brand-text-2">
        <Link
          to="/"
          className={`shrink-0 inline-flex items-center h-8 px-3 rounded-pill ${
            isAgents ? 'bg-white text-brand-ink font-semibold shadow-[0_1px_2px_rgba(26,22,21,0.06)]' : 'hover:bg-brand-ink/5'
          }`}
        >
          Agents
        </Link>
        <Link
          to="/broker"
          className={`shrink-0 inline-flex items-center h-8 px-3 rounded-pill ${
            !isAgents ? 'bg-white text-brand-ink font-semibold shadow-[0_1px_2px_rgba(26,22,21,0.06)]' : 'hover:bg-brand-ink/5'
          }`}
        >
          Brokerages
        </Link>
        <Link to="/upgrade" className="shrink-0 inline-flex items-center h-8 px-3 rounded-pill hover:bg-brand-ink/5">
          Pricing
        </Link>
        <Link to="/walkthrough?tour=auto" className="shrink-0 inline-flex items-center h-8 px-3 rounded-pill hover:bg-brand-ink/5">
          Walkthrough
        </Link>
        <Link to="/blog" className="shrink-0 inline-flex items-center h-8 px-3 rounded-pill hover:bg-brand-ink/5">
          Blog
        </Link>
      </nav>
    </header>
  );
}

// ─── 1. Hero ─────────────────────────────────────────────────────────────────

// Tracks scroll progress (0-1) through a runway section. 0 = section top is
// at viewport top, 1 = section bottom is at viewport bottom. Used by the Hero
// to drive the scroll-pinned document fan-out + home reveal.
function useScrollProgress(ref: RefObject<HTMLElement>): number {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const calc = () => {
      const rect = el.getBoundingClientRect();
      const runway = el.offsetHeight - window.innerHeight;
      if (runway <= 0) {
        setProgress(0);
        return;
      }
      const scrolled = -rect.top;
      setProgress(Math.max(0, Math.min(1, scrolled / runway)));
    };
    calc();
    window.addEventListener('scroll', calc, { passive: true });
    window.addEventListener('resize', calc);
    return () => {
      window.removeEventListener('scroll', calc);
      window.removeEventListener('resize', calc);
    };
  }, [ref]);
  return progress;
}

// Choreography:
//   0.00 – 0.30 : docs fade OUT, house fades IN
//   0.30 – 0.60 : HOLD (docs gone, house full)
//   0.60 – 1.00 : house fades OUT (docs stay gone)
//   1.00+       : empty hero — user scrolls past the runway
// Docs hide once and stay hidden; house bell-curves through the runway.
// On mobile (< md), the runway collapses to 0 — hero renders static.
function houseReveal(progress: number): number {
  if (progress < 0.3) return progress / 0.3;
  if (progress < 0.6) return 1;
  if (progress < 1.0) return Math.max(0, 1 - (progress - 0.6) / 0.4);
  return 0;
}

function docsHide(progress: number): number {
  if (progress < 0.3) return progress / 0.3;
  return 1;
}

function Hero({
  accent,
  content,
}: {
  accent: VariantAccent;
  content: VariantPageContent['hero'];
}) {
  const runwayRef = useRef<HTMLElement>(null);
  const progress = useScrollProgress(runwayRef);
  const house = houseReveal(progress);
  const docs = docsHide(progress);
  return (
    <section
      ref={runwayRef}
      className={`relative bg-gradient-to-b ${accent.heroFrom} ${accent.heroVia} ${accent.heroTo} md:h-[180vh]`}
    >
      <div className="md:sticky md:top-0 md:h-screen overflow-hidden">
        {/* Original colorwave — production blurs */}
        <div className="pointer-events-none absolute inset-0 z-0" aria-hidden>
          <div className="absolute top-6 left-4 md:top-24 md:left-16 flex flex-col gap-2">
            <div className="h-7 w-32 md:w-48 rounded-sm bg-brand-sky/55 blur-2xl" />
            <div className="h-7 w-24 md:w-36 rounded-sm bg-brand-sky/36 blur-2xl" />
            <div className="h-6 w-20 md:w-28 rounded-sm bg-brand-sky-soft/50 blur-2xl" />
          </div>
          <div className="absolute top-12 right-4 md:top-28 md:right-20 flex flex-col gap-2 items-end">
            <div className="h-7 w-32 md:w-48 rounded-sm bg-brand-cream/70 blur-2xl" />
            <div className="h-7 w-24 md:w-36 rounded-sm bg-brand-cream/50 blur-2xl" />
          </div>
        </div>

        <div className="relative z-10 max-w-[1440px] mx-auto px-4 md:px-12 lg:px-20 pt-14 md:pt-24 pb-16 md:pb-24 grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-8 items-center md:h-full">
          {/* LEFT — copy */}
          <div className="max-w-[560px] md:pr-6">
            <div className="inline-flex items-center gap-2 rounded-pill bg-white/70 border border-brand-ink/[0.08] px-3.5 h-7 mb-7">
              <DiscloserMark size={12} />
              <span className="font-meta text-[11px] uppercase tracking-[0.16em] text-brand-text-3">
                {content.eyebrow}
              </span>
            </div>

            <h1 className="text-[44px] md:text-[60px] lg:text-[76px] leading-[0.98] tracking-[-0.03em] text-brand-ink mb-6">
              <span className="block font-brand font-semibold tracking-[-0.025em]">
                {content.h1Lead}
              </span>
              <span className="block font-display italic font-semibold tracking-[-0.025em] mt-1 text-brand-accent animate-breathe">
                {content.h1Italic}
              </span>
            </h1>

            <p className="font-brand text-[16px] md:text-[17px] text-brand-text-2 max-w-[520px] leading-[1.55] mb-9">
              {content.subhead}
            </p>

            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-2">
              <Link
                to={content.ctaHref}
                className={`inline-flex items-center justify-center gap-2 rounded-pill text-white font-brand text-[15px] font-semibold h-12 px-[22px] transition-colors active:scale-[0.97] shadow-[0_10px_30px_-10px_rgba(26,22,21,0.4)] ${accent.ctaFill} ${accent.ctaHover}`}
              >
                {content.ctaLabel} <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/walkthrough?tour=auto"
                className="inline-flex items-center justify-center gap-2 rounded-pill bg-white/70 border border-brand-ink/10 text-brand-ink font-brand text-[15px] font-semibold h-12 px-[22px] transition-colors hover:bg-white hover:border-brand-ink/20 active:scale-[0.97]"
              >
                See the walkthrough <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="mt-3 font-meta text-[12px] text-brand-text-3">
              {content.ctaSubtext}
            </div>
          </div>

          {/* RIGHT — document spread + scroll-revealed home scene behind it */}
          <div className="hidden md:flex items-center justify-center h-[660px] relative">
            {/* Home scene — fades in behind documents during runway, then fades out */}
            <div
              className="absolute inset-0 flex items-center justify-center pointer-events-none"
              style={{
                opacity: house,
                transform: `scale(${0.94 + 0.06 * house})`,
                transformOrigin: 'center center',
                transition: 'opacity 0.15s linear, transform 0.15s linear',
              }}
            >
              <HeroHomeScene
                opacity={1}
                scale={1}
                style={{ width: '110%', maxWidth: 760, aspectRatio: '680 / 700' }}
              />
            </div>
            <HeroDocumentSpread fanProgress={docs} />
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Promo strip (sticky, agents-only) ──────────────────────────────────────

const PROMO_DURATION_MS = 240 * 60 * 60 * 1000;
const PROMO_END_STORAGE_KEY = 'discloser-closeddeals-promo-end';

function getPromoEndTime(): number {
  if (typeof window === 'undefined') return Date.now() + PROMO_DURATION_MS;
  const stored = window.localStorage.getItem(PROMO_END_STORAGE_KEY);
  const parsed = stored ? Number(stored) : NaN;
  if (Number.isFinite(parsed) && parsed > Date.now()) return parsed;
  const end = Date.now() + PROMO_DURATION_MS;
  window.localStorage.setItem(PROMO_END_STORAGE_KEY, String(end));
  return end;
}

function formatPromoCountdown(ms: number): string {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const days = Math.floor(totalSeconds / 86_400);
  const hours = Math.floor((totalSeconds % 86_400) / 3_600);
  const minutes = Math.floor((totalSeconds % 3_600) / 60);
  const seconds = totalSeconds % 60;
  return [days, hours, minutes, seconds]
    .map((value) => String(value).padStart(2, '0'))
    .join(':');
}

function PromoStrip() {
  const [promoEnd] = useState(getPromoEndTime);
  const [now, setNow] = useState(() => Date.now());
  const countdown = formatPromoCountdown(promoEnd - now);
  const ticket = (
    <span className="inline-flex items-center gap-3 px-5 whitespace-nowrap">
      <DiscloserMark size={13} />
      <span>
        Use{' '}
        <code className="font-bold text-brand-ink tracking-wider px-1.5 py-0.5 bg-white/75 border border-brand-ink/[0.08] rounded text-[11.5px] md:text-[12px] tabular-nums">
          CLOSEDEALS
        </code>{' '}
        for 3 months free of Pro
      </span>
      <span className="text-brand-text-3">·</span>
      <span className="font-meta text-[10px] uppercase tracking-[0.14em] text-brand-text-3">
        Ends in
      </span>
      <span className="rounded bg-brand-ink px-2 py-0.5 font-mono text-[11px] font-semibold tabular-nums text-white">
        {countdown}
      </span>
      <span className="text-brand-text-3 hidden md:inline">·</span>
      <span className="line-through decoration-brand-text-3/60 decoration-[1.5px] text-brand-text-3 tabular-nums hidden md:inline">
        $19/mo
      </span>
      <span className="text-brand-text-3 hidden md:inline">→</span>
      <span className="font-bold text-brand-ink tabular-nums text-[13px] md:text-[14px] hidden md:inline">
        $0
      </span>
    </span>
  );

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  return (
    <div className="sticky top-14 z-30 h-11 overflow-hidden border-b border-brand-ink/[0.07] bg-gradient-to-r from-brand-sky/20 via-brand-sky-soft/40 to-brand-sky/20 text-[12px] text-brand-text-2 backdrop-blur md:text-[13px]">
      <style>{`
        @keyframes discloser-promo-ticket {
          from { transform: translate3d(0, 0, 0); }
          to { transform: translate3d(-50%, 0, 0); }
        }
      `}</style>
      <div
        className="flex h-full w-max items-center"
        style={{ animation: 'discloser-promo-ticket 28s linear infinite' }}
        aria-label={`Use CLOSEDEALS for 3 months free of Pro. Promotion ends in ${countdown}.`}
      >
        {ticket}
        {ticket}
        {ticket}
        {ticket}
      </div>
    </div>
  );
}

// ─── Trust strip under the hero ──────────────────────────────────────────────

function TrustStrip() {
  return (
    <section className="bg-brand-paper-warm border-t border-brand-ink/[0.08]">
      <div className="max-w-[1440px] mx-auto px-4 md:px-12 lg:px-20 py-6 md:py-7 flex flex-col md:flex-row items-start md:items-center gap-3 md:gap-8">
        <span className="font-meta text-[10.5px] uppercase tracking-[0.18em] text-brand-text-3 whitespace-nowrap">
          Trusted by agents at
        </span>
        <div className="flex flex-wrap items-center gap-x-9 gap-y-2 opacity-80">
          <span className="font-display text-[15px] text-brand-text-3 tracking-tight">Coldwell Banker</span>
          <span className="font-display text-[15px] text-brand-text-3 tracking-tight">Compass</span>
          <span className="font-display text-[15px] text-brand-text-3 tracking-tight">Sotheby's International</span>
          <span className="font-display text-[15px] text-brand-text-3 tracking-tight">Keller Williams</span>
        </div>
      </div>
    </section>
  );
}

// ─── Hero document spread (V9 right column) ──────────────────────────────────
//
// Editorial illustration: four layered C.A.R. disclosure forms (AVID, SPQ,
// TDS-main with a "Review Required" flagged cluster, NHD). Ink-stroked
// (no rust) per V9 brand call. DiscloserMark stamps mark the agent's
// review trail. Static; purely decorative.

function HeroDocumentSpread({ fanProgress = 0 }: { fanProgress?: number }) {
  // Pure crossfade: documents fade out as the home behind fades in. Base
  // rotations are preserved (V9 layered arrangement) — no fan-out motion.
  // (Prop name kept for callsite stability; semantic is now opacity-out.)
  return (
    <div className="relative w-[520px] h-[660px]" style={{ opacity: 1 - fanProgress }}>
      {/* PAGE 1 — back-left (AVID excerpt) */}
      <DocPage
        positionClass="absolute w-[320px] h-[414px] top-[118px] left-1 z-[1]"
        transform="rotate(-11deg)"
        transformOrigin="bottom right"
        bgClass="bg-[#F8F5F2]"
        innerPad="p-[18px_20px]"
      >
        <DocId left="AVID — Agent Visual Inspection Disclosure" right="C.A.R. Form AVID" />
        <FormHeader>Section C — Visible Defects Observed</FormHeader>
        <FormSubheader>C.1 — Structural / Foundation</FormSubheader>
        <Line w="95%" /><Line w="80%" /><Line w="60%" /><Line w="95%" />
        <FormSubheader>C.2 — Roof / Attic</FormSubheader>
        <Line w="80%" /><Line w="95%" /><Line w="40%" />
        <FormSubheader>C.3 — Electrical / Plumbing</FormSubheader>
        <Line w="95%" /><Line w="60%" /><Line w="80%" /><Line w="95%" /><Line w="40%" /><Line w="80%" /><Line w="95%" />
        <FormSubheader>C.4 — Interior</FormSubheader>
        <Line w="60%" /><Line w="80%" /><Line w="95%" /><Line w="40%" />
        <PageFooter left="Seller's Initials: _____/_____" center="Page 3 of 8" right="© C.A.R. 2024" />
      </DocPage>

      {/* PAGE 2 — back-right (SPQ excerpt) */}
      <DocPage
        positionClass="absolute w-[310px] h-[402px] top-[108px] right-0.5 z-[1]"
        transform="rotate(9deg)"
        transformOrigin="bottom left"
        bgClass="bg-[#F9F6F3]"
        innerPad="p-[18px_20px]"
      >
        <DocId left="Seller Property Questionnaire — C.A.R. SPQ" right="Rev. 12/23" />
        <FormHeader>Part II — Systems &amp; Appliances</FormHeader>
        <FormSubheader>II.B — Plumbing</FormSubheader>
        <Line w="95%" /><Line w="80%" /><Line w="60%" /><Line w="95%" />
        <FormSubheader>II.C — HVAC</FormSubheader>
        <Line w="80%" /><Line w="95%" /><Line w="40%" /><Line w="95%" />
        <FormSubheader>II.D — Pool / Spa</FormSubheader>
        <Line w="60%" /><Line w="80%" /><Line w="95%" /><Line w="40%" />
        <FormSubheader>II.E — Fireplace / Chimney</FormSubheader>
        <Line w="95%" /><Line w="80%" /><Line w="60%" /><Line w="95%" />
        <PageFooter left="Seller's Initials: _____/_____" center="Page 4 of 7" right="© C.A.R. 2024" />
      </DocPage>

      {/* PAGE 3 — MAIN CENTER (TDS p.2 with flagged cluster) */}
      <DocPage
        positionClass="absolute w-[354px] h-[458px] top-[76px] left-1/2 z-[3]"
        transform="translateX(-50%) rotate(-2deg)"
        transformOrigin="center center"
        bgClass="bg-[#FDFCFB]"
        innerPad="p-[26px_24px_22px]"
      >
        <DocId left="Transfer Disclosure Statement — C.A.R. Form TDS" right="Page 2 of 3" />
        <FormHeader>Part II — Seller's Information</FormHeader>
        <FormSubheader>II.A — Mechanical Systems &amp; Safety</FormSubheader>

        <div className="relative border-[1.5px] border-brand-ink rounded-[2px] p-1.5 my-1.5 bg-brand-ink/[0.03]">
          <div className="absolute -top-1.5 left-1.5 bg-brand-ink text-brand-paper-warm font-meta text-[4.5px] font-bold uppercase tracking-[0.1em] px-1 py-px rounded-[1px]">
            Review Required
          </div>
          <FormRow label={<><b>II.A.5</b> Smoke detectors (operable, code-compliant)</>} yn="Y" />
          <FormRow label={<><b>II.A.6</b> Water heater properly strapped / seismically secured per Cal. H&amp;S §19211</>} yn="N" />
          <FormRow label={<><b>II.A.7</b> Carbon monoxide detectors installed per Cal. H&amp;S §17926</>} yn="Y" />
          <FormRow label={<><b>II.A.10</b> Seismic strap / automatic gas shutoff valve present</>} yn="N" last />
        </div>

        <Stamp size={32} top="134px" right="18px" opacity={0.88} />

        <FormSubheader>II.B — Environmental / Hazard Disclosure</FormSubheader>
        <FormRow label={<><b>III.B.1</b> Lead-based paint / hazards known (pre-1978 property)</>} yn="Y" />
        <FormRow label={<><b>III.B.3</b> Asbestos-containing materials present or suspected</>} yn="N" />
        <FormRow label={<><b>III.B.7</b> Property within Alquist-Priolo Earthquake Fault Zone</>} yn="Y" />

        <FormSubheader>II.C — Legal / Title / HOA</FormSubheader>
        <FormRow label={<><b>IV.C.1</b> Property subject to CC&amp;Rs, HOA, or planned development</>} yn="Y" />
        <FormRow label={<><b>IV.C.2</b> HOA current in dues; no pending special assessments &gt;$1,000</>} yn="N" />
        <FormRow label={<><b>IV.C.5</b> Pending litigation involving property or HOA</>} yn="N" last />

        <Stamp size={24} bottom="88px" left="8px" opacity={0.45} />

        <PageFooter left="Seller's Initials: _____/_____" center="C.A.R. Form TDS — Rev. 12/2023" right="Buyer's Initials: _____/_____" />
      </DocPage>

      {/* PAGE 4 — front-right (NHD overlay) */}
      <DocPage
        positionClass="absolute w-[266px] h-[344px] top-[200px] right-[14px] z-[4]"
        transform="rotate(5.5deg)"
        transformOrigin="top left"
        bgClass="bg-[#FEFCFA]"
        innerPad="p-[18px_20px]"
      >
        <DocId left="Natural Hazard Disclosure — NHD" right="Cal. Gov. §1103" />
        <FormHeader>Section III — Statutory Disclosures</FormHeader>
        <FormSubheader>III.1 — Seismic Hazard Zones</FormSubheader>
        <FormRow label={<><b>III.1.A</b> Within a Seismic Hazard Zone (liquefaction / landslide)</>} yn="Y" />
        <FormRow label={<><b>III.1.B</b> Within a Special Flood Hazard Area (FEMA SFHA)</>} yn="N" />
        <FormSubheader>III.2 — Fire Hazard</FormSubheader>
        <FormRow label={<><b>III.2.A</b> Within a State Fire Responsibility Area (SFRA)</>} yn="Y" />
        <FormRow label={<><b>III.2.B</b> Very High Fire Hazard Severity Zone (VHFHSZ)</>} yn="Y" last />
        <Stamp size={40} top="148px" right="6px" opacity={0.82} />
        <FormSubheader>III.3 — Additional Local Disclosures</FormSubheader>
        <Line w="95%" /><Line w="80%" /><Line w="60%" />
        <div className="flex gap-2 mt-2">
          <div className="flex-1">
            <div className="border-b border-brand-ink/25 h-2" />
            <div className="font-meta text-[4.5px] text-brand-ink/40 tracking-[0.06em] mt-0.5">Seller Signature</div>
          </div>
          <div className="flex-1">
            <div className="border-b border-brand-ink/25 h-2" />
            <div className="font-meta text-[4.5px] text-brand-ink/40 tracking-[0.06em] mt-0.5">Date</div>
          </div>
        </div>
        <PageFooter left="Prepared by licensed agent" center="Page 1 of 2" right="NHD-2024" />
      </DocPage>

      {/* Floor anchor */}
      <div
        className="absolute bottom-2.5 left-1/2 -translate-x-1/2 w-[380px] h-[30px] pointer-events-none z-0"
        style={{ background: 'radial-gradient(ellipse at center, rgba(23,19,18,0.12) 0%, transparent 70%)' }}
        aria-hidden
      />
    </div>
  );
}

// ─── Doc-spread sub-primitives ───────────────────────────────────────────────

function DocPage({
  positionClass,
  transform,
  transformOrigin,
  bgClass = 'bg-[#FDFCFB]',
  innerPad,
  children,
}: {
  positionClass: string;
  transform?: string;
  transformOrigin?: string;
  bgClass?: string;
  innerPad: string;
  children: React.ReactNode;
}) {
  return (
    <div
      className={`${positionClass} ${bgClass} border border-brand-ink/[0.12] rounded-[3px] overflow-hidden`}
      style={{
        transform,
        transformOrigin,
        transition: 'transform 0.15s linear',
        boxShadow:
          '0 2px 4px rgba(23,19,18,0.06), 0 8px 24px rgba(23,19,18,0.08), 0 24px 48px rgba(23,19,18,0.04)',
      }}
      aria-hidden
    >
      <div className={`${innerPad} h-full overflow-hidden relative`}>{children}</div>
    </div>
  );
}

function DocId({ left, right }: { left: string; right: string }) {
  return (
    <div className="font-meta text-[5.5px] font-normal tracking-[0.08em] uppercase text-brand-ink/40 mb-2.5 flex justify-between">
      <span>{left}</span>
      <span>{right}</span>
    </div>
  );
}

function FormHeader({ children }: { children: React.ReactNode }) {
  return (
    <div className="font-meta text-[7px] font-bold tracking-[0.14em] uppercase text-brand-ink mb-1 pb-1 border-b-[1.5px] border-brand-ink">
      {children}
    </div>
  );
}

function FormSubheader({ children }: { children: React.ReactNode }) {
  return (
    <div className="font-meta text-[6px] font-medium tracking-[0.1em] uppercase text-brand-ink mt-2 mb-1 opacity-70">
      {children}
    </div>
  );
}

function FormRow({
  label,
  yn,
  last = false,
}: {
  label: React.ReactNode;
  yn: 'Y' | 'N';
  last?: boolean;
}) {
  return (
    <div
      className={`flex items-start gap-1.5 py-[3px] ${
        last ? '' : 'border-b border-brand-ink/[0.1]'
      }`}
      style={!last ? { borderBottomWidth: '0.5px' } : undefined}
    >
      <div className="font-meta text-[6px] leading-[1.4] text-brand-ink flex-1 pr-1">{label}</div>
      <div className="flex gap-[3px] shrink-0 items-center">
        <YnCell on={yn === 'Y'} letter="Y" />
        <YnCell on={yn === 'N'} letter="N" />
      </div>
    </div>
  );
}

function YnCell({ on, letter }: { on: boolean; letter: 'Y' | 'N' }) {
  if (on) {
    return (
      <div
        className={`w-[14px] h-[9px] border flex items-center justify-center font-meta text-[5px] font-bold ${
          letter === 'Y'
            ? 'border-brand-ink bg-brand-ink text-brand-paper-warm'
            : 'border-brand-ink bg-transparent text-brand-ink'
        }`}
        style={{ borderWidth: '0.75px' }}
      >
        {letter}
      </div>
    );
  }
  return (
    <div
      className="w-[14px] h-[9px] border border-brand-ink/35 flex items-center justify-center font-meta text-[5px] font-semibold text-brand-ink/45"
      style={{ borderWidth: '0.75px' }}
    >
      {letter}
    </div>
  );
}

function Line({ w }: { w: '40%' | '60%' | '80%' | '95%' }) {
  return <div className="h-[5px] bg-brand-ink/[0.08] rounded-[1px] my-[3.5px]" style={{ width: w }} />;
}

function PageFooter({ left, center, right }: { left: string; center: string; right: string }) {
  return (
    <div className="absolute bottom-2.5 left-6 right-6 flex justify-between font-meta text-[5px] text-brand-ink/30 tracking-[0.06em]">
      <span>{left}</span>
      <span className="text-center">{center}</span>
      <span>{right}</span>
    </div>
  );
}

function Stamp({
  size,
  top,
  right,
  bottom,
  left,
  opacity,
}: {
  size: number;
  top?: string;
  right?: string;
  bottom?: string;
  left?: string;
  opacity: number;
}) {
  return (
    <div className="absolute z-10" style={{ top, right, bottom, left }} aria-hidden>
      <div style={{ filter: 'drop-shadow(0 1px 3px rgba(23,19,18,0.18))', opacity }}>
        <DiscloserMark size={size} fill="hsl(var(--brand-ink))" />
      </div>
    </div>
  );
}

// ─── 2. Problem ──────────────────────────────────────────────────────────────

function Problem({
  content,
  variant,
}: {
  content: VariantPageContent['problem'];
  variant: VariantKey;
}) {
  // Agents variant gets the V5 chasm diagram. Brokers keeps the simpler
  // centered layout (its narrative is dashboard-driven, visualized in the
  // Solution section's centerpiece).
  const isAgents = variant === 'seller' || variant === 'buyer';
  return (
    <section className="border-y border-black/5 bg-brand-paper-warm">
      <div className="max-w-[1200px] mx-auto px-4 md:px-6 py-16 md:py-24 text-center">
        <div className="inline-flex items-center gap-2.5 mb-5 px-3.5 h-7 border border-brand-ink/[0.08] rounded-pill bg-white">
          <DiscloserMark size={12} />
          <span className="font-meta text-[11px] uppercase tracking-[0.18em] text-brand-text-3 font-medium">
            {content.eyebrow}
          </span>
        </div>
        <h2 className="font-brand text-[32px] md:text-[58px] leading-[1.04] font-semibold text-brand-ink tracking-[-0.025em] max-w-[880px] mx-auto mb-5">
          {content.title}
        </h2>
        <div className="max-w-[780px] mx-auto">{content.body}</div>

        {isAgents && (
          <div className="hidden md:block mt-8">
            <ChasmDiagram />
          </div>
        )}

        <div className="mt-10 font-display italic text-[13.5px] text-brand-text-3">
          {content.handoff}
        </div>
      </div>
    </section>
  );
}

// ─── Chasm diagram — V5 Problem visual ──────────────────────────────────────
//
// Two ledges with the listing agent (left) and buyer's agent (right) standing
// apart, with the disclosure packet falling into the chasm between them —
// "days lost to disclosures." Pure SVG; static. Inline hex colors because
// SVG gradients/strokes don't pick up CSS tokens cleanly.

function ChasmDiagram() {
  return (
    <div className="w-full max-w-[1200px] mx-auto">
      <svg viewBox="0 0 1200 360" xmlns="http://www.w3.org/2000/svg" className="block w-full h-auto">
        <defs>
          <linearGradient id="chasm" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#171312" stopOpacity="0" />
            <stop offset="40%" stopColor="#171312" stopOpacity="0.45" />
            <stop offset="100%" stopColor="#171312" stopOpacity="0.92" />
          </linearGradient>
          <linearGradient id="ledge-l" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#C7D9EE" />
            <stop offset="100%" stopColor="#9EBFE5" />
          </linearGradient>
          <linearGradient id="ledge-r" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#F0CCAF" />
            <stop offset="100%" stopColor="#D6A07A" />
          </linearGradient>
          <pattern id="hatch" patternUnits="userSpaceOnUse" width="6" height="6" patternTransform="rotate(45)">
            <line x1="0" y1="0" x2="0" y2="6" stroke="#171312" strokeOpacity="0.08" strokeWidth="2" />
          </pattern>
        </defs>

        {/* LEFT LEDGE */}
        <path d="M 0 200 L 430 200 L 460 235 L 0 235 Z" fill="url(#ledge-l)" />
        <path d="M 0 235 L 460 235 L 460 360 L 0 360 Z" fill="url(#hatch)" opacity="0.7" />
        <line x1="0" y1="200" x2="430" y2="200" stroke="#171312" strokeOpacity="0.12" strokeWidth="1" />

        {/* RIGHT LEDGE */}
        <path d="M 770 200 L 1200 200 L 1200 235 L 740 235 Z" fill="url(#ledge-r)" />
        <path d="M 740 235 L 1200 235 L 1200 360 L 740 360 Z" fill="url(#hatch)" opacity="0.7" />
        <line x1="770" y1="200" x2="1200" y2="200" stroke="#171312" strokeOpacity="0.12" strokeWidth="1" />

        {/* CHASM DEPTH */}
        <path d="M 460 235 L 740 235 L 740 360 L 460 360 Z" fill="url(#chasm)" opacity="0.85" />

        {/* FIGURES — left agent (listing) */}
        <g transform="translate(160,80)">
          <circle cx="0" cy="22" r="22" fill="#3D7AB5" />
          <path d="M -30 105 L -30 70 Q -30 48 0 48 Q 30 48 30 70 L 30 105 Z" fill="#3D7AB5" />
          <rect x="-30" y="105" width="60" height="15" fill="#3D7AB5" opacity="0.6" />
        </g>
        <text x="160" y="225" textAnchor="middle" fontFamily="DM Sans" fontSize="11" letterSpacing="2.4" fill="#171312" opacity="0.55" style={{ textTransform: 'uppercase' }}>
          JOHN · LISTING AGENT
        </text>
        <text x="160" y="183" textAnchor="middle" fontFamily="DM Sans" fontStyle="italic" fontSize="14" fill="#171312" opacity="0.7">
          "not live yet…"
        </text>

        {/* FIGURES — right agent (buyer) */}
        <g transform="translate(1040,80)">
          <circle cx="0" cy="22" r="22" fill="#3D7AB5" />
          <path d="M -30 105 L -30 70 Q -30 48 0 48 Q 30 48 30 70 L 30 105 Z" fill="#3D7AB5" />
          <rect x="-30" y="105" width="60" height="15" fill="#3D7AB5" opacity="0.6" />
        </g>
        <text x="1040" y="225" textAnchor="middle" fontFamily="DM Sans" fontSize="11" letterSpacing="2.4" fill="#171312" opacity="0.55" style={{ textTransform: 'uppercase' }}>
          MARIA · BUYER'S AGENT
        </text>
        <text x="1040" y="183" textAnchor="middle" fontFamily="DM Sans" fontStyle="italic" fontSize="14" fill="#171312" opacity="0.7">
          "still reading the packet…"
        </text>

        {/* BROKEN BRIDGE arrows */}
        <path d="M 220 110 Q 360 60 480 110" stroke="#3D7AB5" strokeWidth="1.6" fill="none" strokeDasharray="4 6" opacity="0.6" />
        <path d="M 480 110 L 472 104 M 480 110 L 472 116" stroke="#3D7AB5" strokeWidth="1.6" fill="none" opacity="0.6" />

        <path d="M 980 110 Q 840 60 720 110" stroke="#3D7AB5" strokeWidth="1.6" fill="none" strokeDasharray="4 6" opacity="0.6" />
        <path d="M 720 110 L 728 104 M 720 110 L 728 116" stroke="#3D7AB5" strokeWidth="1.6" fill="none" opacity="0.6" />

        {/* FALLING DEAL */}
        <g transform="translate(600,178)">
          <rect x="-40" y="-26" width="80" height="52" rx="3" fill="#FAF7F5" stroke="#171312" strokeOpacity="0.18" strokeWidth="1.2" transform="rotate(8)" />
          <line x1="-26" y1="-12" x2="22" y2="-6" stroke="#171312" strokeOpacity="0.35" strokeWidth="1" transform="rotate(8)" />
          <line x1="-26" y1="-4" x2="18" y2="2" stroke="#171312" strokeOpacity="0.35" strokeWidth="1" transform="rotate(8)" />
          <line x1="-26" y1="4" x2="14" y2="10" stroke="#171312" strokeOpacity="0.35" strokeWidth="1" transform="rotate(8)" />
          <text x="0" y="-32" textAnchor="middle" fontFamily="DM Sans" fontStyle="italic" fontSize="13" fill="#171312" opacity="0.7">
            the packet
          </text>
        </g>

        {/* Falling trail */}
        <path d="M 600 178 Q 612 240 620 320" stroke="#171312" strokeWidth="1.2" fill="none" strokeDasharray="2 5" opacity="0.45" />
        <text x="600" y="346" textAnchor="middle" fontFamily="DM Sans" fontSize="11" letterSpacing="3.6" fill="#FAF7F5" opacity="0.6" style={{ textTransform: 'uppercase' }}>
          — DAYS LOST TO DISCLOSURES —
        </text>

        {/* Labels under ledges */}
        <text x="220" y="262" textAnchor="middle" fontFamily="DM Sans" fontStyle="italic" fontSize="13" fill="#171312" opacity="0.55">
          300 pages to file
        </text>
        <text x="220" y="280" textAnchor="middle" fontFamily="DM Sans" fontSize="10.5" letterSpacing="1.4" fill="#171312" opacity="0.45" style={{ textTransform: 'uppercase' }}>
          Days to list →
        </text>

        <text x="980" y="262" textAnchor="middle" fontFamily="DM Sans" fontStyle="italic" fontSize="13" fill="#171312" opacity="0.55">
          300 pages to read
        </text>
        <text x="980" y="280" textAnchor="middle" fontFamily="DM Sans" fontSize="10.5" letterSpacing="1.4" fill="#171312" opacity="0.45" style={{ textTransform: 'uppercase' }}>
          Days to offer →
        </text>
      </svg>
    </div>
  );
}

// ─── 3. Solution ─────────────────────────────────────────────────────────────

const STAGE_ICON: Record<StageIcon, React.ComponentType<{ className?: string; strokeWidth?: number }>> = {
  doc: FileText,
  msg: MessageSquare,
  shield: ShieldCheck,
  eye: Eye,
  users: Users,
};

function Solution({
  variant,
  accent,
  content,
}: {
  variant: VariantKey;
  accent: VariantAccent;
  content: VariantPageContent['solution'];
}) {
  // V6 — Asymmetric Anchor — for the Agents variant: Stage 02 (the handoff)
  // dominates as a 1.7fr brand-anchor card with a rust rail + DiscloserMark
  // watermark; Stages 01/03 stack as 1fr side cards. Brokers keeps the
  // 3-up + dashboard centerpiece — its narrative shape is different.
  const isAgents = variant === 'seller' || variant === 'buyer';
  return (
    <section id="how" className="bg-brand-paper-warm">
      <div className="max-w-[1200px] mx-auto px-4 md:px-6 py-16 md:py-24">
        {isAgents ? (
          <>
            <div className="flex justify-center mb-7 md:mb-8">
              <div className="inline-flex items-center gap-2 rounded-pill bg-white/70 border border-brand-ink/[0.08] px-3.5 h-7">
                <DiscloserMark size={12} />
                <span className="font-meta text-[11px] uppercase tracking-[0.16em] text-brand-text-3">
                  {content.eyebrow}
                </span>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-[1fr_320px] gap-6 md:gap-12 items-end mb-12 md:mb-14 pb-6 border-b border-brand-ink/[0.08]">
              <h2 className="font-brand text-[34px] md:text-[52px] leading-[1.04] font-semibold text-brand-ink tracking-[-0.025em] max-w-[760px]">
                {content.title}
              </h2>
              <div className="font-display italic text-[14px] text-brand-text-3 md:text-right leading-snug">
                Three stages.<br />One Closer.
              </div>
            </div>
          </>
        ) : (
          <div className="text-center mb-14 md:mb-16">
            <div className="font-meta text-[11px] uppercase tracking-[0.16em] text-brand-text-3 mb-4">
              {content.eyebrow}
            </div>
            <h2 className="font-display text-[32px] md:text-[44px] leading-[1.05] font-semibold text-brand-ink tracking-tight max-w-3xl mx-auto">
              {content.title}
            </h2>
          </div>
        )}

        {isAgents ? (
          <div className="grid grid-cols-1 md:grid-cols-[1.7fr_1fr] gap-6 md:gap-7 items-stretch">
            <AnchorCard stage={content.stages[1]} />
            <div className="flex flex-col gap-6 md:gap-7">
              <SideCard num="Stage 01" stage={content.stages[0]} accent={accent} />
              <SideCard num="Stage 03" stage={content.stages[2]} accent={accent} />
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 mb-12 md:mb-16 items-start">
            {content.stages.map((stage, idx) => {
              const Icon = STAGE_ICON[stage.icon];
              return (
                <StageCard
                  key={idx}
                  icon={<Icon className={`w-5 h-5 ${accent.iconText}`} strokeWidth={2.2} />}
                  title={stage.title}
                  body={stage.body}
                />
              );
            })}
          </div>
        )}

        {variant === 'efficiency' && (
          <div className="rounded-2xl border border-black/5 bg-white p-6 md:p-10 max-w-[880px] mx-auto">
            <div className="flex items-center gap-2 font-meta text-[10px] uppercase tracking-[0.14em] text-brand-text-3 mb-3">
              <Sparkles className={`w-3.5 h-3.5 ${accent.iconText}`} strokeWidth={2.4} />
              {content.centerpiece.label}
            </div>
            <div className="font-brand text-[14px] text-brand-text-3 mb-4">
              {content.centerpiece.setup}
            </div>
            <div className="font-meta text-[10px] uppercase tracking-[0.14em] text-brand-text-3 mb-2">
              {content.centerpiece.replyLabel}
            </div>
            <blockquote className="border-l-2 border-brand-ink/30 pl-5 font-brand text-[15.5px] md:text-base text-brand-ink leading-relaxed">
              {content.centerpiece.reply}
            </blockquote>
            <div className="mt-6 font-brand text-[13.5px] text-brand-text-2 leading-snug">
              {content.centerpiece.footer}
            </div>
          </div>
        )}

        <div className="mt-12 text-center font-meta text-[12.5px] text-brand-text-3 italic">
          {content.handoff}
        </div>
      </div>
    </section>
  );
}

function AnchorCard({ stage }: { stage: Stage }) {
  return (
    <article className="relative overflow-hidden rounded-2xl bg-white border border-brand-ink/[0.10] p-8 md:p-12 shadow-[0_40px_80px_-42px_rgba(26,22,21,0.32)] flex flex-col">
      <div className="absolute inset-y-0 left-0 w-1 bg-brand-accent" aria-hidden />
      <div
        className="absolute -right-24 -top-24 w-[460px] h-[460px] pointer-events-none"
        style={{
          background:
            'radial-gradient(circle at center, hsl(var(--brand-accent) / 0.10) 0%, transparent 65%)',
        }}
        aria-hidden
      />
      <span className="self-start inline-flex items-center gap-2 rounded-pill bg-brand-ink text-white px-3 h-7 font-meta text-[10px] uppercase tracking-[0.16em] font-semibold mb-5">
        <DiscloserMark size={11} fill="hsl(var(--brand-accent))" />
        Where deals move
      </span>
      <div className="font-display italic text-[15px] text-brand-accent mb-2">
        Stage 02 — The handoff
      </div>
      <h3 className="font-display text-[30px] md:text-[42px] leading-[1.06] font-semibold text-brand-ink tracking-tight mb-5 max-w-[600px]">
        {stage.title}.
      </h3>
      {stage.lede && (
        <p className="font-display italic text-[17px] md:text-[19px] text-brand-ink-soft leading-snug mb-5 max-w-[620px]">
          {stage.lede}
        </p>
      )}
      <p className="font-brand text-[15px] text-brand-text-2 leading-relaxed max-w-[600px]">
        {stage.body}
      </p>
      <div
        className="absolute right-8 bottom-8 opacity-30 pointer-events-none hidden md:block"
        aria-hidden
      >
        <DiscloserMark size={92} fill="hsl(var(--brand-accent))" />
      </div>
    </article>
  );
}

function SideCard({
  num,
  stage,
  accent,
}: {
  num: string;
  stage: Stage;
  accent: VariantAccent;
}) {
  const Icon = STAGE_ICON[stage.icon];
  return (
    <article className="flex flex-col flex-1 rounded-2xl bg-white border border-brand-ink/[0.08] p-6 md:p-7">
      <div className="font-display italic text-[13px] text-brand-text-3 mb-2">{num}</div>
      <div className="inline-flex items-center gap-3 mb-3">
        <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-brand-paper-warm border border-brand-ink/[0.08]">
          <Icon className={`w-[18px] h-[18px] ${accent.iconText}`} strokeWidth={2.2} />
        </span>
        <h4 className="font-display text-[20px] md:text-[22px] font-semibold text-brand-ink tracking-tight">
          {stage.title}
        </h4>
      </div>
      <p className="font-brand text-[13.5px] text-brand-text-2 leading-relaxed">{stage.body}</p>
    </article>
  );
}

function StageCard({
  icon,
  title,
  body,
}: {
  icon: React.ReactNode;
  title: string;
  body: React.ReactNode;
}) {
  return (
    <div className="rounded-2xl bg-white p-6 md:p-7 [contain:layout_style] transition-shadow border border-black/5">
      <div className="flex items-center gap-2 mb-3">
        <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-brand-paper-warm border border-black/5">
          {icon}
        </span>
        <h3 className="font-display text-[20px] font-semibold text-brand-ink tracking-tight">
          {title}
        </h3>
      </div>
      <p className="font-brand text-[14.5px] text-brand-text-2 leading-relaxed">{body}</p>
    </div>
  );
}

// ─── 4. Proof ────────────────────────────────────────────────────────────────

function Proof({
  content,
  variant,
}: {
  content: VariantPageContent['proof'];
  variant: VariantKey;
}) {
  const isAgents = variant === 'seller' || variant === 'buyer';
  if (isAgents) return <ProofV6 content={content} />;
  return <ProofLegacy content={content} />;
}

const JAKE_SERPA_TESTIMONIAL_IMAGE = '/images/landing/jake-serpa-testimonial.jpg';

// V6 testimonial-frame layout: white frame-board with corner stat tiles, a
// prominent customer photo, italic Playfair pullquote, attribution row, and a
// 4-dot rotator (static — rotation logic not implemented; seed data only
// includes the one verified testimonial).
function ProofV6({ content }: { content: VariantPageContent['proof'] }) {
  return (
    <section className="relative bg-brand-paper-warm">
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse 60% 50% at 50% 45%, rgba(158,191,229,0.14) 0%, transparent 65%)',
        }}
        aria-hidden
      />
      <div className="relative max-w-[1280px] mx-auto px-4 md:px-12 py-20 md:py-28 flex flex-col items-center">
        <div className="inline-flex items-center gap-2 mb-10 px-3.5 h-7 bg-white/70 border border-brand-ink/[0.08] rounded-pill">
          <DiscloserMark size={12} />
          <span className="font-meta text-[10.5px] uppercase tracking-[0.16em] text-brand-text-3 font-medium">
            {content.eyebrow}
          </span>
        </div>

        <div className="relative w-full max-w-[1080px] bg-white border border-brand-ink/[0.08] rounded-[18px] shadow-[0_30px_80px_-40px_rgba(26,22,21,0.25)] p-8 md:p-[30px]">
          <div className="absolute inset-3.5 border border-brand-sky/45 rounded-[10px] pointer-events-none" aria-hidden />

          {/* Corner stat tiles — pulled outside the frame on md+ */}
          {content.stats[0] && (
            <StatTile className="md:-top-8 md:-left-8 top-2 left-2 md:absolute static mb-3 md:mb-0" stat={content.stats[0]} />
          )}
          {content.stats[1] && (
            <StatTile className="md:-top-8 md:-right-8 top-2 right-2 md:absolute static mb-3 md:mb-0" stat={content.stats[1]} />
          )}
          {content.stats[2] && (
            <StatTile className="md:-bottom-8 md:-left-8 bottom-2 left-2 md:absolute static mb-3 md:mb-0" stat={content.stats[2]} />
          )}
          {content.stats[3] && (
            <StatTile className="md:-bottom-8 md:-right-8 bottom-2 right-2 md:absolute static mb-3 md:mb-0" stat={content.stats[3]} />
          )}

          <div className="relative grid gap-7 md:grid-cols-[minmax(280px,0.9fr)_minmax(0,1.1fr)] md:items-stretch px-4 md:px-9 py-12 md:py-10">
            <div className="relative overflow-hidden rounded-[14px] bg-brand-paper-warm border border-brand-ink/[0.08] min-h-[360px] md:min-h-[560px]">
              <img
                src={JAKE_SERPA_TESTIMONIAL_IMAGE}
                alt="Jake Serpa"
                className="absolute inset-0 h-full w-full object-cover object-[50%_18%]"
                loading="lazy"
              />
            </div>

            <div className="flex flex-col justify-center text-left md:pl-4 lg:pl-8 md:pr-4 py-2 md:py-8">
              <div className="font-display italic text-[14px] text-brand-text-3 mb-5">
                {content.title}
              </div>
              <div className="font-display italic text-[78px] md:text-[104px] leading-[0.6] text-brand-sky -mb-4 tracking-[-0.03em]">
                &ldquo;
              </div>
              <p className="font-display italic text-[30px] md:text-[43px] lg:text-[50px] leading-[1.12] tracking-[-0.022em] text-brand-ink font-normal max-w-[560px] mb-9">
                {renderEmphasisedQuote(content.quote, content.quoteEmphasis)}
              </p>
              <div className="inline-flex w-fit items-center pt-6 border-t border-brand-ink/[0.08]">
                <div className="text-left">
                  <div className="font-brand text-[17px] md:text-[18px] font-semibold text-brand-ink tracking-[-0.01em]">
                    {content.quoteAuthorName ?? content.quoteAttribution.replace(/^[—\s-]+/, '')}
                  </div>
                  {content.quoteAuthorRole && (
                    <div className="font-brand text-[13.5px] md:text-[14.5px] font-semibold uppercase tracking-[0.08em] text-brand-accent mt-1">
                      {content.quoteAuthorRole}
                    </div>
                  )}
                </div>
              </div>

              {/* Rotator affordance — 4 dots, first active. Static (no carousel
                  logic) — placeholders for 3 future testimonials. */}
              <div className="flex items-center gap-[18px] mt-6" role="tablist" aria-label="Testimonials">
                <div className="flex items-center gap-2">
                  <RotatorDot active label="Testimonial 1 of 4" />
                  <RotatorDot label="Testimonial 2 of 4" />
                  <RotatorDot label="Testimonial 3 of 4" />
                  <RotatorDot label="Testimonial 4 of 4" />
                </div>
                <span className="font-meta text-[10.5px] uppercase tracking-[0.14em] text-brand-text-3 tabular-nums">
                  01 / 04
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-14 flex flex-col items-center gap-1.5">
          <div className="font-meta text-[9.5px] uppercase tracking-[0.22em] text-brand-text-3 font-medium">
            In the hands of agents at
          </div>
          <div className="flex flex-wrap items-baseline justify-center gap-x-4 gap-y-1 opacity-65">
            <span className="font-display text-[17px] text-brand-text-2 tracking-tight">Coldwell Banker</span>
            <span className="text-brand-text-3">·</span>
            <span className="font-display text-[17px] text-brand-text-2 tracking-tight">Compass</span>
            <span className="text-brand-text-3">·</span>
            <span className="font-display text-[17px] text-brand-text-2 tracking-tight">Sotheby's International</span>
          </div>
        </div>

        <div className="mt-9 font-display italic text-[12.5px] text-brand-text-3">
          {content.handoff}
        </div>
      </div>
    </section>
  );
}

function StatTile({ stat, className = '' }: { stat: { number: string; label: string; italic?: boolean }; className?: string }) {
  return (
    <div
      className={`bg-brand-paper border border-brand-ink/[0.08] rounded-xl p-[18px_22px] min-w-[200px] max-w-[240px] ${className}`}
    >
      <div
        className={`text-[34px] md:text-[42px] leading-none font-display font-semibold text-brand-ink mb-1.5 tracking-[-0.02em] tabular-nums ${
          stat.italic ? 'italic font-normal' : ''
        }`}
      >
        {stat.number}
      </div>
      <div className="font-brand text-[11.5px] text-brand-text-2 leading-snug max-w-[200px]">
        {stat.label}
      </div>
    </div>
  );
}

function RotatorDot({ active = false, label }: { active?: boolean; label: string }) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      aria-label={label}
      className={`h-1.5 rounded-full border-0 p-0 transition-[width,background-color] duration-200 ${
        active ? 'w-[22px] bg-brand-ink' : 'w-1.5 bg-brand-ink/20 hover:bg-brand-text-2'
      }`}
    />
  );
}

function renderEmphasisedQuote(quote: string, emphasis?: string): React.ReactNode {
  if (!emphasis) return quote;
  const idx = quote.indexOf(emphasis);
  if (idx === -1) return quote;
  return (
    <>
      {quote.slice(0, idx)}
      <strong
        className="font-display font-semibold not-italic px-1"
        style={{ background: 'linear-gradient(180deg,transparent 60%,rgba(158,191,229,0.55) 60%)' }}
      >
        {emphasis}
      </strong>
      {quote.slice(idx + emphasis.length)}
    </>
  );
}

// Legacy proof layout — kept for Brokers variant (2-stat shape + simple
// quote card). Use Tailwind tokens directly so we don't depend on V6 setup.
function ProofLegacy({ content }: { content: VariantPageContent['proof'] }) {
  return (
    <section className="border-y border-black/5 bg-white/40">
      <div className="max-w-[1100px] mx-auto px-4 md:px-6 py-16 md:py-24">
        <div className="text-center mb-12 md:mb-14">
          <div className="font-meta text-[11px] uppercase tracking-[0.16em] text-brand-text-3 mb-4">
            {content.eyebrow}
          </div>
          <h2 className="font-display text-[32px] md:text-[44px] leading-[1.05] font-semibold text-brand-ink tracking-tight max-w-2xl mx-auto">
            {content.title}
          </h2>
        </div>

        <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-4 mb-12 md:mb-14 opacity-70">
          <BrokerageLogoText label="Coldwell Banker" />
          <BrokerageLogoText label="Compass" />
          <BrokerageLogoText label="Sotheby's International" />
        </div>

        <div
          className={`grid grid-cols-1 gap-6 md:gap-8 mx-auto mb-14 ${
            content.stats.length === 2
              ? 'md:grid-cols-2 max-w-xl'
              : 'md:grid-cols-3 max-w-3xl'
          }`}
        >
          {content.stats.map((stat, idx) => (
            <StatCell key={idx} number={stat.number} label={stat.label} />
          ))}
        </div>

        <div className="max-w-2xl mx-auto rounded-2xl border border-black/5 bg-white p-7 md:p-8">
          <QuoteIcon className="w-6 h-6 text-brand-text-3 mb-4" strokeWidth={1.8} />
          <p className="font-display text-[18px] md:text-[20px] leading-relaxed text-brand-ink mb-4 italic">
            {content.quote}
          </p>
          <div className="font-meta text-[12px] text-brand-text-3">
            {content.quoteAttribution}
          </div>
        </div>

        <div className="mt-10 text-center font-meta text-[12.5px] text-brand-text-3 italic">
          {content.handoff}
        </div>
      </div>
    </section>
  );
}

function BrokerageLogoText({ label }: { label: string }) {
  return (
    <span className="font-display text-[18px] md:text-[20px] text-brand-text-3 tracking-tight">
      {label}
    </span>
  );
}

function StatCell({ number, label }: { number: string; label: string }) {
  return (
    <div className="text-center">
      <div className="font-display text-[40px] md:text-[52px] leading-none font-semibold text-brand-ink mb-2">
        <span className="inline-block min-w-[6ch] tabular-nums">
          {number}
        </span>
      </div>
      <div className="font-brand text-[13px] text-brand-text-2 leading-snug max-w-[200px] mx-auto">
        {label}
      </div>
    </div>
  );
}

// ─── 5. Final CTA ────────────────────────────────────────────────────────────

function FinalCTA({
  accent,
  content,
  variant,
}: {
  accent: VariantAccent;
  content: VariantPageContent['finalCta'];
  variant: VariantKey;
}) {
  const isAgents = variant === 'seller' || variant === 'buyer';
  if (isAgents) return <FinalCTAV1 accent={accent} content={content} />;
  return <FinalCTALegacy accent={accent} content={content} />;
}

// V1 centered-classic: topline rule + Playfair title with italic emphasis +
// subhead + big CTA pill + 3 horizontal numbered pills + PS chip + colophon.
function FinalCTAV1({
  accent,
  content,
}: {
  accent: VariantAccent;
  content: VariantPageContent['finalCta'];
}) {
  return (
    <section
      className={`relative overflow-hidden bg-gradient-to-b ${accent.finalFrom} ${accent.finalVia} ${accent.finalTo}`}
    >
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse 80% 60% at 50% 0%, rgba(236,221,208,0.45) 0%, transparent 70%), radial-gradient(ellipse 60% 50% at 50% 100%, rgba(158,191,229,0.18) 0%, transparent 60%)',
        }}
        aria-hidden
      />
      <div className="relative max-w-[1100px] mx-auto px-4 md:px-12 lg:px-20 pt-20 md:pt-24 pb-16 md:pb-20">
        <div className="flex flex-col items-center text-center">
          <h2 className="font-brand text-[40px] md:text-[64px] lg:text-[80px] leading-[1.02] font-semibold tracking-[-0.025em] text-brand-ink max-w-[880px] mb-5">
            {content.title}
          </h2>
          <p className="font-brand text-[16px] md:text-[17.5px] text-brand-text-2 max-w-[560px] leading-[1.55] mb-9">
            {content.subhead}
          </p>
          <Link
            to={content.ctaHref}
            className={`inline-flex items-center gap-2.5 rounded-pill text-white font-brand text-[15.5px] font-semibold h-[52px] px-7 transition-colors active:scale-[0.97] shadow-[0_14px_38px_-12px_rgba(26,22,21,0.45)] ${accent.ctaFill} ${accent.ctaHover}`}
          >
            {content.ctaLabel} <ArrowRight className="w-3.5 h-3.5" />
          </Link>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 w-full max-w-[920px] mt-12 md:mt-14">
            <OnboardingPill n={1}>
              <b className="text-brand-ink font-semibold">Drop the packet.</b> Any folder, any size.
            </OnboardingPill>
            <OnboardingPill n={2}>
              <b className="text-brand-ink font-semibold">Closer scans it.</b> TDS · SPQ · AVID.
            </OnboardingPill>
            <OnboardingPill n={3}>
              <b className="text-brand-ink font-semibold">Both sides ready,</b> same afternoon.
            </OnboardingPill>
          </div>

          <p className="mt-14 max-w-[560px] font-display italic text-[14.5px] leading-[1.65] text-brand-text-2 pt-5 border-t border-brand-ink/[0.08]">
            <span className="font-meta not-italic font-semibold text-[10.5px] tracking-[0.2em] uppercase text-brand-text-3 mr-2">
              PS
            </span>
            {content.psLine}
          </p>
        </div>

        <div className="mt-14 flex justify-center font-meta text-[10px] uppercase tracking-[0.22em] text-brand-text-3">
          <span>Discloser · California-built</span>
        </div>
      </div>
    </section>
  );
}

function OnboardingPill({ n, children }: { n: number; children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-3.5 px-5 py-4 bg-white border border-brand-ink/[0.08] rounded-pill">
      <div className="w-7 h-7 rounded-full bg-brand-paper flex items-center justify-center font-display italic font-semibold text-[14px] text-brand-ink shrink-0">
        {n}
      </div>
      <div className="font-brand text-[13.5px] text-brand-text-2 leading-snug font-medium">
        {children}
      </div>
    </div>
  );
}

// Legacy final-CTA layout — kept for Brokers (sales-led, no self-serve pill row).
function FinalCTALegacy({
  accent,
  content,
}: {
  accent: VariantAccent;
  content: VariantPageContent['finalCta'];
}) {
  return (
    <section
      className={`relative overflow-hidden bg-gradient-to-b ${accent.finalFrom} ${accent.finalVia} ${accent.finalTo}`}
    >
      <div className="relative max-w-[860px] mx-auto px-4 md:px-6 py-20 md:py-28 text-center">
        <h2 className="font-display text-[36px] md:text-[56px] leading-[1.02] font-semibold text-brand-ink tracking-tight mb-5">
          {content.title}
        </h2>
        <p className="font-brand text-base md:text-lg text-brand-text-2 max-w-xl mx-auto mb-8">
          {content.subhead}
        </p>
        <Link
          to={content.ctaHref}
          className={`inline-flex items-center gap-2 rounded-pill text-white font-brand text-base font-semibold h-12 px-6 transition-colors active:scale-[0.97] shadow-[0_10px_30px_-10px_rgba(26,22,21,0.4)] ${accent.ctaFill} ${accent.ctaHover}`}
        >
          {content.ctaLabel} <ArrowRight className="w-4 h-4" />
        </Link>

        <div className="mt-12 max-w-md mx-auto font-brand text-[13.5px] text-brand-text-2 leading-relaxed border-t border-brand-ink/10 pt-6">
          {content.psLine}
        </div>
      </div>
    </section>
  );
}

// ─── Footer ──────────────────────────────────────────────────────────────────

function MarketingFooter() {
  return (
    <footer className="border-t border-black/5 bg-brand-paper-warm">
      <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-8 flex flex-col md:flex-row gap-4 md:gap-6 items-start md:items-center">
        <div className="flex items-center gap-2">
          <DiscloserMark size={22} />
          <span className="font-display text-base font-semibold text-brand-ink">Discloser</span>
        </div>
        <div className="font-meta text-xs text-brand-text-3">
          © {new Date().getFullYear()} Discloser, Inc. California-built AI for real estate
          transactions.
        </div>
        <div className="md:ml-auto flex flex-wrap items-center gap-x-5 gap-y-3 font-meta text-xs text-brand-text-2">
          <Link to="/" className="hover:text-brand-ink">
            Agents
          </Link>
          <Link to="/broker" className="hover:text-brand-ink">
            Brokerages
          </Link>
          <Link to="/upgrade" className="hover:text-brand-ink">
            Pricing
          </Link>
          <Link to="/blog" className="hover:text-brand-ink">
            Blog
          </Link>
          <a href="mailto:sales@discloser.co" className="hover:text-brand-ink">
            Contact
          </a>
          <a href="/privacy" className="hover:text-brand-ink">
            Privacy
          </a>
          <a href="/terms" className="hover:text-brand-ink">
            Terms
          </a>
        </div>
      </div>
    </footer>
  );
}
